<template>
	<!-- TODO doplnit notifikaciu so zmenou stavu -->
	<div v-if="activeMainQuest" class="task-tracker-wrapper">
		<div
			class="task-tracker"
			:class="{ 'task-tracker--completed': questCompleted }"
			@click="redirectToQuests"
		>
			<div
				class="quest-type"
				:class="{ 'quest-type--completed': questCompleted }"
			>
				<AppNotification
					v-if="showNotification"
					class="quest-type__notification"
					value="!"
					:type="StatusTypes.WARNING"
				/>
			</div>
			<span
				v-html="
					`<div class='task-tracker__task'><span>${replaceGamePassPlaceholders(translate(activeMainQuest.assignment))}</span></div> <span>(${formatNumber(progress)}/${formatNumber(getAmount(activeMainQuest.objective.amount))})</span>`
				"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions.ts";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import { quests } from "@/game/entities/config/quests/quests";
import { settlementNames } from "@/game/entities/config/settlements";
import { hasLevel, hasSettlementId, hasType } from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import useQuestStore from "@/store/useQuestStore";
import type { QuestActive } from "@/types/quest";
import { RouteNames, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { getAmount } from "@/utils/number";
import { canTranslate, translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, watch } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const soundService = markRaw(SoundService.getInstance());

const questStore = useQuestStore();
const { trackedQuestId, getActiveQuests } = storeToRefs(questStore);

const showNotification = computed((): boolean => {
	const activeQuest =
		(activeMainQuest.value?.objective.amount as number) ?? 100000;
	return progress.value >= activeQuest;
});

// TODO napojit na spravny quest, toto je docasne na testovacie ucely
const activeMainQuest = computed((): QuestActive | null => {
	if (trackedQuestId.value === null) return null;
	return (
		getActiveQuests.value.find(
			(q: QuestActive): boolean => q.id === trackedQuestId.value,
		) || null
	);
});

const questCompleted = computed((): boolean => {
	if (!activeMainQuest.value) {
		return false;
	}
	if (
		activeMainQuest.value.objective.action ===
		QuestObjectiveActions.USE_RESOURCES
	) {
		return !!activeMainQuest.value.payed;
	}
	return (
		activeMainQuest.value.progress >=
		getAmount(activeMainQuest.value.objective.amount)
	);
});

const progress = computed((): number => {
	if (!activeMainQuest.value) {
		return 0;
	}
	return activeMainQuest.value.progress >=
		getAmount(activeMainQuest.value.objective.amount)
		? getAmount(activeMainQuest.value.objective.amount)
		: activeMainQuest.value.progress;
});
const handleIcon = (quest: QuestActive): string => {
	if (!hasType(quest.objective)) return "";
	// If type is a Set, return ""
	if (quest.objective.type instanceof Set) return "";
	return (quest.objective.type as string).toLowerCase();
};

const getTownName = (quest: QuestActive): string => {
	if (!quest || !quest.objective || !hasSettlementId(quest.objective)) {
		return "";
	}

	const actualSettlement = settlementNames.get(quest.objective.settlement_id);
	if (!actualSettlement) {
		return "";
	}

	return actualSettlement.name;
};

const replaceGamePassPlaceholders = (text: string): string => {
	const quest = activeMainQuest.value;
	if (!quest) {
		return "";
	}

	const townName = getTownName(quest);

	const building = hasType(quest.objective) ? quest.objective.type : "";
	let buildingKey = "";
	if (building instanceof Set) {
		buildingKey = `${Array.from(building)[0]}.name`;
	} else {
		buildingKey = `${building}.name`;
	}

	const replacements = {
		"{number}": `${formatNumber(getAmount(quest.objective.amount))}`,
		"{2to4}": `${formatNumber(getAmount(quest.objective.amount))}`,
		"{level}": `${hasLevel(quest.objective) ? formatNumber(quest.objective.level) : formatNumber(getAmount(quest.objective.amount))}`,
		"{percent}": `${formatNumber(getAmount(quest.objective.amount))}%`,
		"{icon}": `<div class='icon icon-${handleIcon(quest)}-14'/>`,
		"{building}": canTranslate(buildingKey) ? translate(buildingKey) : "",
		"{town}": `${townName}`,
	} as Record<string, string>;

	let str = text;

	str = str.replace(/{\w+}/g, (all: string): string => {
		return replacements[all] || all;
	});

	return str;
};

const redirectToQuests = (): void => {
	if (!activeMainQuest.value) {
		return;
	}
	soundService.playGenerickClick();

	router.push({
		name: RouteNames.QUESTS,
		params: {
			// don't forget that if we don't have at least a default value for params, it won't be properly reloaded after refresh
			tab:
				quests.get(trackedQuestId.value ?? 0)?.type === QuestTypes.SIDE
					? 1
					: 0, // main
			// NOTE: if the in-progress active quest is displayed, show modal-window
			// NOTE: if it is pending, claim or generally tutorial, go through popup
			showPopup: "false",
		},
	});
};

// Watch the questCompleted and if it turns true console log
watch(
	(): boolean => questCompleted.value,
	(newValue: boolean): void => {
		if (newValue) {
			soundService.playQuestFinish();
		}
	},
);
</script>

<style lang="scss" scoped>
.task-tracker {
	position: relative;
	cursor: pointer;
	margin-left: 0.5rem;
	background: linear-gradient(
		to right,
		rgba(17, 17, 17, 0.9),
		rgba(17, 17, 17, 0.7),
		transparent
	);
	padding: 0rem 1.688rem 0.25rem 2.625rem;
	width: fit-content;
	max-width: calc(100vw - 3.5rem);

	&--completed {
		border-width: 0.063rem;
		border-style: solid;
		border-right: 0;
		border-image-source: linear-gradient(
			to right,
			#1dcf50,
			rgba(29, 207, 80, 0.9),
			transparent
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to right,
			#005218,
			rgba(0, 82, 24, 0.9),
			rgba(0, 82, 24, 0.7),
			transparent
		);
	}

	span {
		display: flex;
		padding: 0.1rem 0;
		color: #f2dcb5;
		font-size: 0.813rem;
		line-height: 1rem;
	}

	.quest-type {
		position: absolute;
		top: 50%;
		left: -0.5rem;
		transform: translateY(-50%);
		width: 2.688rem;
		height: 2.688rem;
		@include background(
			url($path-images + "quests/quest-task-tracker-img.avif"),
			contain,
			center,
			no-repeat
		);
		&--completed {
			@include background(
				url($path-images + "quests/quest-task-tracker-img-completed.avif"),
				contain,
				center,
				no-repeat
			);
		}
		&__notification {
			position: absolute;
			top: -0.2rem;
			right: -0.2rem;
		}
	}

	:deep(.icon) {
		position: relative;
		top: 0.15rem;
		margin: 0.1rem 0.25rem 0 0.1rem;
	}

	:deep(.task-tracker__task) {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-right: 0.25rem;
	}
}
</style>
